import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Flame, Trophy, Target, Sparkles, Users, Crown, HelpCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useMotivationStore } from "../store/motivationStore";
import { Progress } from "../components/ui/progress";

/**
 * Motivasyon (Gamification) sayfası — XP + Level + Streak + Rozetler +
 * Haftalık Meydan Okumalar + Şirket Hedefi + Liderlik Tablosu.
 */
export default function Motivasyon() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { stats, level, badges, catalog, fetchMe } = useMotivationStore();
  const [leaderboard, setLeaderboard] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [goal, setGoal] = useState(null);

  useEffect(() => {
    fetchMe();
    (async () => {
      try {
        const [lb, ch, cg] = await Promise.all([
          api.get("/motivation/leaderboard"),
          api.get("/motivation/challenges"),
          api.get("/motivation/company-goal"),
        ]);
        setLeaderboard(lb.data);
        setChallenges(ch.data);
        setGoal(cg.data);
      } catch {
        /* ignore */
      }
    })();
  }, [fetchMe]);

  if (!stats || !level) {
    return (
      <div className="py-20 text-center text-sm text-zinc-500">
        Yükleniyor...
      </div>
    );
  }

  const earnedCodes = new Set(badges.map((b) => b.code));
  const xpProgress =
    level.next_min && level.next_min !== level.level_min
      ? Math.min(
          100,
          ((stats.xp - level.level_min) /
            (level.next_min - level.level_min)) *
            100,
        )
      : 100;

  return (
    <div className="space-y-8" data-testid="motivation-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">
            {t("nav.motivation")}
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            Motivasyon &amp; Rozetler
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Görev tamamlayarak XP kazan, seviye atla, rozet topla.
          </p>
        </div>
        <Link
          to="/app/motivasyon-kurallari"
          data-testid="motivation-rules-link"
          className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
        >
          <HelpCircle className="h-3.5 w-3.5" />
          Nasıl çalışır?
        </Link>
      </div>

      {/* Level + Streak overview */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div
          data-testid="motivation-level-card"
          className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50 p-6 shadow-sm"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-700">
            <Trophy className="h-4 w-4" /> Seviye
          </div>
          <div className="mt-2 flex items-center gap-3">
            <TierBadge tier={level.tier} />
            <div>
              <div
                className="font-display text-2xl font-semibold text-zinc-900"
                data-testid="motivation-level-name"
              >
                {level.level}
              </div>
              <div className="text-xs text-zinc-500">
                {stats.xp} XP
                {level.next_level ? ` · ${level.next_min - stats.xp} XP kaldı` : " · Max"}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Progress value={xpProgress} className="h-2 bg-amber-100" />
            {level.next_level && (
              <div className="mt-1 text-[11px] text-zinc-500">
                Sıradaki: <b>{level.next_level}</b>
              </div>
            )}
          </div>
        </div>

        <div
          data-testid="motivation-streak-card"
          className="rounded-xl border border-red-200 bg-gradient-to-br from-red-50 via-white to-red-50 p-6 shadow-sm"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-red-700">
            <Flame className="h-4 w-4" /> Streak
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              className="font-display text-4xl font-semibold text-red-600"
              data-testid="motivation-streak-days"
            >
              {stats.streak_days}
            </span>
            <span className="text-sm text-zinc-500">gün</span>
          </div>
          <div className="mt-2 text-xs text-zinc-500">
            En uzun streak: <b>{stats.longest_streak}</b> gün
          </div>
          {stats.streak_days >= 3 && (
            <div className="mt-2 text-xs font-medium text-red-700">
              🔥 {stats.streak_days} gün üst üste görev tamamladın!
            </div>
          )}
        </div>

        <div
          data-testid="motivation-tasks-card"
          className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 p-6 shadow-sm"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-700">
            <Target className="h-4 w-4" /> Toplam
          </div>
          <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
            <Stat label="Görev" value={stats.tasks_completed} />
            <Stat label="Zamanında" value={stats.on_time_completions} />
            <Stat label="Zorlu" value={stats.high_prio_completions} />
            <Stat label="Toplantı" value={stats.meeting_yes_count} />
          </div>
        </div>
      </div>

      {/* Company goal */}
      {goal && (
        <div
          data-testid="motivation-company-goal"
          className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
            <Sparkles className="h-4 w-4 text-amber-500" /> Şirket Hedefi ({goal.period})
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-2xl font-semibold text-zinc-900">
              {goal.current_xp}
            </span>
            <span className="text-sm text-zinc-500">/ {goal.target_xp} XP</span>
          </div>
          <Progress
            value={Math.min(100, (goal.current_xp / goal.target_xp) * 100)}
            className="mt-3 h-2"
          />
          {goal.top_contributors?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                En çok katkı:
              </span>
              {goal.top_contributors.map((c, i) => (
                <span
                  key={c.user_id}
                  className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-700"
                >
                  {i === 0 && <Crown className="h-3 w-3 text-amber-500" />}
                  {c.name}
                  <b className="text-amber-700">+{c.xp}</b>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Weekly challenges */}
      {challenges.length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
            <Target className="h-4 w-4 text-blue-500" /> Bu Haftaki Meydan Okumalar
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {challenges.map((c) => (
              <div
                key={c.code}
                data-testid={`challenge-${c.code}`}
                className={[
                  "rounded-lg border p-4",
                  c.completed
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-zinc-200 bg-zinc-50",
                ].join(" ")}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-zinc-900">
                    {c.title}
                  </div>
                  <div className="text-[11px] font-semibold text-amber-700">
                    +{c.reward_xp} XP
                  </div>
                </div>
                <div className="mt-2">
                  <Progress
                    value={Math.min(
                      100,
                      (c.progress / Math.max(1, c.target)) * 100,
                    )}
                    className="h-2"
                  />
                  <div className="mt-1 text-[11px] text-zinc-500">
                    {c.progress} / {c.target}
                    {c.completed && (
                      <span className="ml-2 font-medium text-emerald-700">
                        ✓ Tamamlandı
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Badges */}
      <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
          <Sparkles className="h-4 w-4 text-purple-500" /> Rozetler ·{" "}
          {badges.length} / {catalog.length}
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
          {catalog.map((b) => {
            const earned = earnedCodes.has(b.code);
            return (
              <div
                key={b.code}
                data-testid={`badge-${b.code}`}
                className={[
                  "rounded-lg border p-3 text-center transition-transform",
                  earned
                    ? "border-amber-300 bg-gradient-to-br from-amber-50 to-white shadow-sm hover:-translate-y-0.5"
                    : "border-zinc-200 bg-zinc-50 opacity-60 grayscale",
                ].join(" ")}
              >
                <div className="text-3xl">{b.emoji}</div>
                <div className="mt-1 text-sm font-semibold text-zinc-900">
                  {b.name}
                </div>
                <div className="mt-0.5 text-[10px] text-zinc-500">
                  {b.desc}
                </div>
                {earned && (
                  <div className="mt-2 text-[10px] font-medium text-emerald-700">
                    ✓ Kazanıldı
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Leaderboard */}
      <div
        data-testid="motivation-leaderboard"
        className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm"
      >
        <div className="flex items-center gap-2 border-b border-zinc-100 bg-zinc-50 px-6 py-3 text-xs uppercase tracking-wider text-zinc-500">
          <Users className="h-4 w-4 text-zinc-500" /> Liderlik Tablosu
        </div>
        {leaderboard.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-400">
            Henüz veri yok.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 text-left text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                <th className="px-6 py-3">#</th>
                <th className="px-6 py-3">Kullanıcı</th>
                <th className="px-6 py-3">Seviye</th>
                <th className="px-6 py-3 text-right">XP</th>
                <th className="px-6 py-3 text-right">Görev</th>
                <th className="px-6 py-3 text-right">Streak</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((row) => (
                <tr
                  key={row.user_id}
                  data-testid={`leaderboard-row-${row.user_id}`}
                  className={[
                    "border-b border-zinc-50 last:border-b-0",
                    row.is_me ? "bg-amber-50" : "",
                  ].join(" ")}
                >
                  <td className="px-6 py-3">
                    {row.rank === 1 && <span className="text-lg">🥇</span>}
                    {row.rank === 2 && <span className="text-lg">🥈</span>}
                    {row.rank === 3 && <span className="text-lg">🥉</span>}
                    {row.rank > 3 && (
                      <span className="text-zinc-500">#{row.rank}</span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-200 text-xs font-semibold text-zinc-700">
                        {row.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <span
                        className={row.is_me ? "font-semibold" : ""}
                      >
                        {row.name}
                        {row.is_me && (
                          <span className="ml-1 text-[10px] text-amber-700">
                            (sen)
                          </span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <TierChip tier={row.level.tier} label={row.level.level} />
                  </td>
                  <td className="px-6 py-3 text-right font-semibold text-amber-700">
                    {row.xp}
                  </td>
                  <td className="px-6 py-3 text-right text-zinc-700">
                    {row.tasks_completed}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-red-600">
                      <Flame className="h-3 w-3" />
                      {row.streak_days}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-400">
        {label}
      </div>
      <div className="text-xl font-semibold text-zinc-900">{value}</div>
    </div>
  );
}

const TIER_STYLES = {
  bronze: "bg-orange-200 text-orange-900",
  silver: "bg-zinc-200 text-zinc-800",
  gold: "bg-amber-300 text-amber-900",
  platinum: "bg-cyan-200 text-cyan-900",
  diamond: "bg-purple-200 text-purple-900",
  legend: "bg-gradient-to-r from-amber-400 to-pink-400 text-white",
};

function TierBadge({ tier }) {
  return (
    <div
      className={[
        "flex h-12 w-12 items-center justify-center rounded-full text-xl font-bold ring-2 ring-white shadow-md",
        TIER_STYLES[tier] || TIER_STYLES.bronze,
      ].join(" ")}
    >
      🏆
    </div>
  );
}

function TierChip({ tier, label }) {
  return (
    <span
      className={[
        "inline-block rounded-full px-2 py-0.5 text-[11px] font-medium",
        TIER_STYLES[tier] || TIER_STYLES.bronze,
      ].join(" ")}
    >
      {label}
    </span>
  );
}
